// Animated water for the FLO river: a fragment shader drawn with expo-gl
// (WebGL on web, OpenGL ES on device). It paints the land, the banks and the
// moving water in one pass; the bank curve is the SAME formula FloRiver's
// `bankAt` uses, so the SVG hairlines, columns and orbs line up on top.
//
// Flow is left → right (INTAKE → LATER): layered noise scrolls along the
// meander, the centre runs deeper and darker, there is a sun glint on the
// ripples and a little foam where the water meets the banks.
import React, { useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import { GLView, type ExpoWebGLRenderingContext } from 'expo-gl';

export interface RiverWaterProps {
  width: number;
  height: number;
  waterTop: number;
  waterBottom: number;
  intakeWidth: number;
  laterWidth: number;
  /** false → one still frame (reduced motion). */
  animate?: boolean;
}

const VERT = `
attribute vec2 a_pos;
void main() { gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

const FRAG = `
precision highp float;
uniform vec2 u_res;
uniform float u_time;
uniform float u_top;
uniform float u_bottom;
uniform float u_intakeW;
uniform float u_laterW;

float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
vec2 hash2(vec2 p) { return fract(sin(vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)))) * 43758.5453); }
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x), mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0, 1.0)), f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0;
  float a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise(p);
    p = p * 2.02 + vec2(13.0, 7.0);
    a *= 0.5;
  }
  return v;
}
// Caustic cells: distance to the nearest of a jittered, drifting point grid.
float caustic(vec2 p, float t) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float m = 1.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 g = vec2(float(x), float(y));
      vec2 o = hash2(i + g);
      o = 0.5 + 0.45 * sin(t * 0.9 + 6.2831 * o);
      m = min(m, length(g + o - f));
    }
  }
  return pow(1.0 - smoothstep(0.0, 0.42, m), 2.2);
}
// Sunlight net on the bed: bright where two Voronoi cells meet (f2 − f1 small).
float causticNet(vec2 p, float t) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  float f1 = 8.0;
  float f2 = 8.0;
  for (int y = -1; y <= 1; y++) {
    for (int x = -1; x <= 1; x++) {
      vec2 g = vec2(float(x), float(y));
      vec2 o = hash2(i + g);
      o = 0.5 + 0.42 * sin(t * 0.8 + 6.2831 * o);
      float dd = length(g + o - f);
      if (dd < f1) { f2 = f1; f1 = dd; } else if (dd < f2) { f2 = dd; }
    }
  }
  return pow(1.0 - smoothstep(0.0, 0.16, f2 - f1), 2.0);
}
// The water surface as a height field: broad swells, finer ripples and two
// trains of small waves, all travelling downstream (−x in flow space).
float height(vec2 p, float t) {
  float h = 0.0;
  h += 0.55 * fbm(p * 1.2 + vec2(-t * 0.22, 0.0));
  h += 0.28 * fbm(p * 3.0 + vec2(-t * 0.50, t * 0.06) + 5.0);
  h += 0.12 * sin(p.x * 8.0 - t * 2.4 + p.y * 1.5);
  h += 0.07 * sin(p.x * 14.0 - t * 3.8 + sin(p.y * 5.0 + t * 0.7) * 1.2);
  h += 0.05 * sin(p.x * 23.0 - t * 5.5 + p.y * 7.0);
  h += 0.10 * (fbm(p * 7.0 + vec2(-t * 1.1, t * 0.1) + 9.0) - 0.5);
  return h;
}

void main() {
  vec2 px = vec2(gl_FragCoord.x, u_res.y - gl_FragCoord.y);
  float x = px.x;
  float y = px.y;
  float span = u_bottom - u_top;
  float amp = min(span * 0.025, u_res.x * 0.02);
  float halfW = span * 0.5 * 0.92;
  float mid = (u_top + u_bottom) * 0.5 + amp * sin(x / u_res.x * 6.2831853 * 1.15 + 0.6);
  float h = halfW;
  float d = (y - mid) / h;
  float ad = abs(d);
  float inside = 1.0 - smoothstep(0.985, 1.015, ad);

  float t = u_time;
  float s = 4.2 / max(1.0, min(span, u_res.x * 0.9));
  vec2 uv = vec2(x, y - mid) * s;

  // Surface normal from the height field (finite differences).
  float e = 0.02;
  float h0 = height(uv, t);
  float hx = height(uv + vec2(e, 0.0), t);
  float hy = height(uv + vec2(0.0, e), t);
  vec3 n = normalize(vec3(-(hx - h0) / e * 0.34, -(hy - h0) / e * 0.34, 1.0));

  vec3 L = normalize(vec3(-0.45, -0.65, 0.62));   // sun up-left, low
  vec3 V = vec3(0.0, 0.0, 1.0);
  vec3 H = normalize(L + V);
  float diff = max(0.0, dot(n, L));
  float spec = pow(max(0.0, dot(n, H)), 120.0);
  float gloss = pow(max(0.0, dot(n, H)), 14.0);
  float fres = pow(1.0 - max(0.0, dot(n, V)), 3.0);

  float depth = smoothstep(0.0, 1.0, 1.0 - ad);          // deeper mid-stream
  // Turquoise, bright and clear (Joel's reference: sunlit pool water).
  vec3 deep = vec3(0.09, 0.50, 0.62);
  vec3 body = vec3(0.20, 0.68, 0.76);
  vec3 shallow = vec3(0.42, 0.84, 0.86);
  vec3 col = mix(shallow, mix(body, deep, depth), smoothstep(0.0, 0.45, depth));
  col *= 0.82 + 0.30 * diff;
  // The white light net, two scales, drifting downstream and wobbling.
  vec2 wob = vec2(fbm(uv * 1.5 + vec2(-t * 0.3, 0.0)), fbm(uv * 1.5 + vec2(-t * 0.25, 4.0) + 2.0)) - 0.5;
  float net1 = causticNet(uv * 1.35 + wob * 0.7 + vec2(-t * 0.22, 0.0), t);
  float net2 = causticNet(uv * 2.4 + wob * 0.5 + vec2(-t * 0.34, 1.7), t * 1.25);
  float net = net1 * 0.42 + net2 * 0.22;
  col += vec3(0.92, 1.0, 1.0) * net * (0.75 + 0.35 * diff);
  // Sheen at grazing angles and the sun sparkle on the ripples.
  vec3 sky = vec3(0.80, 0.95, 1.0);
  col = mix(col, sky, fres * 0.35);
  col += vec3(1.0) * gloss * 0.10;
  col += vec3(1.0, 1.0, 0.95) * spec * 1.3;
  // Foam / bright wash on the shoreline.
  float edge = smoothstep(0.80, 0.985, ad);
  float foam = edge * smoothstep(0.45, 0.80, fbm(uv * 4.0 + vec2(-t * 0.7, 0.0)));
  col = mix(col, vec3(0.94, 0.98, 1.0), foam * 0.6);

  // Land: dark night ground, a wet sandy bank at the edge.
  vec3 land = mix(vec3(0.06, 0.08, 0.15), vec3(0.025, 0.035, 0.07), y / u_res.y);
  land += (noise(px * 0.08) - 0.5) * 0.025;
  float shore = smoothstep(1.0, 1.07, ad);
  vec3 bank = vec3(0.42, 0.34, 0.20);
  vec3 outside = mix(bank, land, shore);

  gl_FragColor = vec4(mix(outside, col, inside), 1.0);
}
`;

export function RiverWater({ width, height, waterTop, waterBottom, intakeWidth, laterWidth, animate = true }: RiverWaterProps) {
  const raf = useRef<number | null>(null);
  const alive = useRef(true);
  const propsRef = useRef({ width, height, waterTop, waterBottom, intakeWidth, laterWidth, animate });
  propsRef.current = { width, height, waterTop, waterBottom, intakeWidth, laterWidth, animate };

  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (raf.current != null) cancelAnimationFrame(raf.current);
    };
  }, []);

  const onContextCreate = (gl: ExpoWebGLRenderingContext) => {
    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        console.warn('[RiverWater] shader', gl.getShaderInfoLog(sh));
      }
      return sh;
    };
    const prog = gl.createProgram()!;
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, 'a_pos');
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, 'u_res');
    const uTime = gl.getUniformLocation(prog, 'u_time');
    const uTop = gl.getUniformLocation(prog, 'u_top');
    const uBottom = gl.getUniformLocation(prog, 'u_bottom');
    const uIntake = gl.getUniformLocation(prog, 'u_intakeW');
    const uLater = gl.getUniformLocation(prog, 'u_laterW');

    const t0 = Date.now();
    const frame = () => {
      if (!alive.current) return;
      const p = propsRef.current;
      const W = gl.drawingBufferWidth;
      const H = gl.drawingBufferHeight;
      // Device pixels per layout pixel, so the bank maths matches the SVG.
      const k = p.width > 0 ? W / p.width : 1;
      gl.viewport(0, 0, W, H);
      gl.uniform2f(uRes, W, H);
      gl.uniform1f(uTime, p.animate ? (Date.now() - t0) / 1000 : 7.3);
      gl.uniform1f(uTop, p.waterTop * k);
      gl.uniform1f(uBottom, p.waterBottom * k);
      gl.uniform1f(uIntake, Math.max(1, p.intakeWidth * k));
      gl.uniform1f(uLater, Math.max(1, p.laterWidth * k));
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      gl.flush();
      gl.endFrameEXP();
      if (p.animate) raf.current = requestAnimationFrame(frame);
    };
    frame();
  };

  return (
    <View style={[StyleSheet.absoluteFill, { overflow: 'hidden' }]} pointerEvents="none">
      <GLView key={`${Math.round(width)}x${Math.round(height)}`} style={{ width, height }} onContextCreate={onContextCreate} />
    </View>
  );
}

export default RiverWater;
